// store/modules/agency.js
import apiClient from "../../plugins/axios";
import router from "../../router";

export default {
  namespaced: true,
  state: () => ({
    agency: null,
    logData: {},
    loading: false,
    customers: [],
    stripeCustomer: null,
    subusers: [],
    emailTemplates: [],
    emailConnections: [],
    whatsAppConnectionStats: null,
    agencyDialog: false,
    dialogReturnRoute: "/",
  }),
  mutations: {
    setAgency(state, agency) {
      state.agency = agency;
    },
    setLogData(state, logData) {
      state.logData = logData;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setAgencyDialog(state, agencyDialog) {
      state.agencyDialog = agencyDialog;
    },
    setCustomers(state, customers) {
      state.customers = customers;
    },
    setStripeCustomer(state, stripeCustomer) {
      state.stripeCustomer = stripeCustomer;
    },
    setSubusers(state, subusers) {
      state.subusers = subusers;
    },
    setEmailTemplates(state, emailTemplates) {
      state.emailTemplates = emailTemplates;
    },
    setEmailConnections(state, emailConnections) {
      state.emailConnections = emailConnections;
    },
    setWhatsAppConnectionStats(state, whatsAppConnectionStats) {
      state.whatsAppConnectionStats = whatsAppConnectionStats;
    },
    setDialogReturnRoute(state, route) {
      state.dialogReturnRoute = route;
    },
    addLogEntry(state, entry) {
      if (!state.logData) {
        state.logData = { changes: [] }; // Initialize logData if not present
      }
      if (!state.logData.changes) {
        state.logData.changes = []; // Ensure changes array exists
      }
      state.logData.changes.push(entry);
    },
    resetAgencyDialog(state) {
      state.agency = null;
      state.logData = {};
      state.customers = [];
      state.subusers = [];
      state.emailTemplates = [];
      state.emailConnections = [];
      state.whatsAppConnectionStats = null;
      state.stripeCustomer = null;
      state.freeSlots = null;
    },
  },
  actions: {
    // Fetches all the data for the agency dialog dont use anywhere else
    async fetchAgencyData({ commit, dispatch }, agencyId) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getAgencyById?agencyId=${agencyId}`
        );
        commit("setAgency", response.data);
        dispatch("fetchLogData", agencyId);
        dispatch("fetchCustomersNamesAndStates", agencyId);
        dispatch("fetchStripeCustomer", agencyId);
        dispatch("fetchSubusers", agencyId);
        dispatch("fetchEmailTemplates", agencyId);
        dispatch("fetchEmailConnections", agencyId);
        dispatch("fetchWhatsAppConnectionStats", agencyId);
        dispatch("calculateSlotAmount");
      } catch (error) {
        console.error("Failed to fetch agency data:", error);
      } finally {
        commit("setLoading", false);
      }
    },

    // can be called alone
    async fetchLogData({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getLogByAgencyId?agencyId=${state.agency._id}`
        );
        commit("setLogData", response.data);
      } catch (error) {
        console.error("Failed to fetch log data:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    // can be called alone
    async fetchCustomersNamesAndStates({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getCustomersNamesAndStates?agencyId=${state.agency._id}`
        );
        commit("setCustomers", response.data);
      } catch (error) {
        console.error("Failed to fetch customer names and state:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    // can be called alone
    async fetchStripeCustomer({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.post(
          "/getters/getStripeCustomerByID",
          {
            agencyId: state.agency._id,
          }
        );
        commit("setStripeCustomer", response.data);
      } catch (error) {
        console.error("Failed to fetch stripe customer:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchSubusers({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getSubusersByAgencyId?agencyId=${state.agency._id}`
        );
        commit("setSubusers", response.data);
      } catch (error) {
        console.error("Failed to fetch subusers:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchEmailTemplates({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getEmailTemplatesByAgencyId?agencyId=${state.agency._id}`
        );
        commit("setEmailTemplates", response.data);
      } catch (error) {
        console.error("Failed to fetch email templates:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchEmailConnections({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getEmailConnectionsByAgencyId?agencyId=${state.agency._id}`
        );
        commit("setEmailConnections", response.data);
      } catch (error) {
        console.error("Failed to fetch email connections:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchWhatsAppConnectionStats({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await apiClient.get(
          `/getters/getWhatsAppConnectionStatsByAgencyId?agencyId=${state.agency._id}`
        );
        commit("setWhatsAppConnectionStats", response.data);
      } catch (error) {
        console.error("Failed to fetch WhatsApp connection statistics:", error);
        // Set empty stats on error
        commit("setWhatsAppConnectionStats", {
          connections: [],
          totalConnections: 0,
          activeConnections: 0,
          totalMessages: 0,
          totalConversations: 0,
        });
      } finally {
        commit("setLoading", false);
      }
    },
    async saveLog(
      { commit, rootState },
      { agencyId, changes = [], description, rating }
    ) {
      commit("setLoading", true);

      const logEntry = {
        agencyId,
        changes,
        description,
        rating,
        lastModified: new Date(),
        user: rootState.loginAndOut.name, // Fetch the name of the admin from the state
      };

      // Make the API call to save the log entry
      try {
        const response = await apiClient.post("/logs/save", logEntry);
        console.log("Response:", response);
        if (response.data.log) {
          commit("setLogData", response.data.log); // Update the logData with the data returned from the server
          console.info("Log saved successfully");
        } else {
          console.error("Failed to save log:", response.data.message);
        }
      } catch (error) {
        console.error("Error saving log data:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    async changeBetaAccess({ commit }, { agencyId, betaAccess }) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        betaAccess,
      };
      // Make the API call to change beta access
      try {
        const response = await apiClient.post(
          "/actions/changeBetaAccess",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Beta access successfully changed");
          return response.data.agency; // Return the updated agency
        } else {
          console.error("Failed to change beta access:", response.data.message);
          return null;
        }
      } catch (error) {
        console.error("Error changing beta access:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async changeWhatsAppAccess({ commit }, { agencyId, whatsAppAccess }) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        whatsAppAccess,
      };
      // Make the API call to change WhatsApp access
      try {
        const response = await apiClient.post(
          "/actions/changeBetaAccess/whatsApp",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("WhatsApp access successfully changed");
          return response.data.agency; // Return the updated agency
        } else {
          console.error(
            "Failed to change WhatsApp access:",
            response.data.message
          );
          return null;
        }
      } catch (error) {
        console.error("Error changing WhatsApp access:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async changePortalBuilderAccess({ commit }, { agencyId, portalBuilderAccess }) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        portalBuilderAccess,
      };
      // Make the API call to change portal builder access
      try {
        const response = await apiClient.post(
          "/actions/changeBetaAccess/portalBuilder",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Portal builder access successfully changed");
          return response.data.agency; // Return the updated agency
        } else {
          console.error(
            "Failed to change portal builder access:",
            response.data.message
          );
          return null;
        }
      } catch (error) {
        console.error("Error changing portal builder access:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async changeMail({ commit }, { agencyId, newMail }) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        newMail,
      };
      // Make the API call to change the email
      try {
        const response = await apiClient.post(
          "/actions/changeMail",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Email successfully changed");
          return response.data.agency; // Return the updated agency
        } else {
          console.error("Failed to change email:", response.data.message);
          return null;
        }
      } catch (error) {
        console.error("Error changing email:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async changeSlotAmount(
      { commit, dispatch },
      { agencyId, newAmountOfSlots, subID }
    ) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        newAmountOfSlots,
        subID,
      };
      // Make the API call to change the slot amount & get the new amount of free slots
      try {
        const response = await apiClient.post(
          "/actions/changeSlotAmmount",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Slot amount successfully changed");
          dispatch("calculateSlotAmount");
          return response.data; // returns agency and activated and deactivated customers
        } else {
          console.error("Failed to change slot amount:", response.data.message);
          return null;
        }
      } catch (error) {
        console.error("Error changing slot amount:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async grantTrial(
      { commit, dispatch },
      { agencyId, amountSlots, trialDays }
    ) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
        amountSlots,
        trialDays,
      };
      // Make the API call to grant the trial
      try {
        const response = await apiClient.post(
          "/actions/grantTrial",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Trial successfully granted");
          dispatch("calculateSlotAmount");
          return response; // returns agency and activated and deactivated customers
        } else {
          console.error("Failed to grant trial:", response.message);
          return null;
        }
      } catch (error) {
        console.error("Error granting trial:", error);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },
    async cancelSubscription({ commit, dispatch }, agencyId) {
      commit("setLoading", true);
      const requestBody = {
        agencyId,
      };
      // Make the API call to cancel the subscription
      try {
        const response = await apiClient.post(
          "/actions/cancelSubscription",
          requestBody
        );
        if (response.data.agency) {
          commit("setAgency", response.data.agency); // Update the agency data with the data returned from the server
          console.info("Subscription successfully canceled");
          dispatch("calculateSlotAmount");
          return response; // returns agency and activated and deactivated customers
        } else {
          console.error("Failed to cancel subscription:", response.message);
          return null;
        }
      } catch (error) {
        console.error("Error canceling subscription:", error);
        return null;
      }
    },
    async flushWholeCache({ commit }) {
      commit("setLoading", true);
      // Make the API call to flush the cache
      try {
        const response = await apiClient.post("/actions/flushCache");
        console.log("Response:", response.data);
        return response;
      } catch (error) {
        console.error("Error flushing cache:", error);
      } finally {
        commit("setLoading", false);
      }
    },
    closeAgencyDialog({ commit, state }) {
      router.replace(state.dialogReturnRoute);
      commit("resetAgencyDialog");
      commit("setAgencyDialog", false);
    },
    resetAgencyDialog({ commit }) {
      commit("resetAgencyDialog");
    },
    setDialogReturnRoute({ commit }, route) {
      commit("setDialogReturnRoute", route);
    },
  },
};

